import type { ReactNode } from 'react';

type EyebrowColor = 'gold' | 'green' | 'purple';

interface SectionHeaderProps {
  /** Small uppercase marker above the title. */
  eyebrow?: string;
  eyebrowColor?: EyebrowColor;
  title: string;
  /** Optional muted lead paragraph under the title. */
  intro?: string;
  /** Optional right-aligned action (e.g. a "ver todos" link/button). */
  action?: ReactNode;
  align?: 'left' | 'center';
  as?: 'h2' | 'h3';
  /** Render light text (use inside an `ink` section). */
  onInk?: boolean;
  className?: string;
}

const eyebrowColorClass: Record<EyebrowColor, string> = {
  gold: 'text-secondary',
  green: 'text-tertiary',
  purple: 'text-primary',
};

// Canonical section heading: eyebrow marker + Fraunces title + optional intro
// and trailing action. Keeps every section's header visually consistent.
export default function SectionHeader({
  eyebrow,
  eyebrowColor = 'gold',
  title,
  intro,
  action,
  align = 'left',
  as: Heading = 'h2',
  onInk = false,
  className = '',
}: SectionHeaderProps) {
  const centered = align === 'center';
  return (
    <div
      className={`mb-10 flex flex-col gap-4 md:mb-12 ${
        action ? 'md:flex-row md:items-end md:justify-between' : ''
      } ${centered ? 'items-center text-center' : ''} ${className}`}
    >
      <div className={centered ? 'max-w-2xl' : ''}>
        {eyebrow && (
          <p
            className={`mb-2 text-xs font-bold tracking-[0.14em] uppercase ${
              onInk ? 'text-gold-leaf' : eyebrowColorClass[eyebrowColor]
            }`}
          >
            {eyebrow}
          </p>
        )}
        <Heading
          className={`font-headline text-3xl font-semibold md:text-4xl ${
            onInk ? 'text-[#fbf8f1]' : 'text-on-surface'
          }`}
        >
          {title}
        </Heading>
        {intro && (
          <p
            className={`mt-3 max-w-2xl text-base ${onInk ? 'text-[#c2b9a6]' : 'text-on-surface-light'}`}
          >
            {intro}
          </p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}
